import { configureStore } from "@reduxjs/toolkit";
import { EMPTY_OBJECT, isDevelopment } from "core/utils";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage"; // defaults to localStorage for web

import { GlobalErrorEvents } from "src/errorHandling";
import { RTKMiddleware } from "../rtk/rtk";
import { getRootReducer } from "./reducer";
import { notificationListener } from "./state/notifications/notificationsListener";

const persistConfig = {
    key: "root",
    storage,
    blacklist: ["api", "authApi", "dynamicPropertiesLoading"],
};

/**
 * Creates redux store with root reducer.
 * Sets up top level error handling for root reducer.
 */
const createStore = () => {
    const rootReducer = getRootReducer();
    const errorHandlingReducer: typeof rootReducer = (state, action) => {
        try {
            return rootReducer(state, action);
        } catch (error) {
            GlobalErrorEvents.triggerReducerErrorEvent(error);
            /* Error at this place is unrecoverable, app cannot be used anymore and ErrorPage is displayed.
             * For this reason we can return previous state or empty object and type cast it. */
            return (state ?? EMPTY_OBJECT) as StoreState;
        }
    };

    const persistedReducer = persistReducer(persistConfig, errorHandlingReducer);

    return configureStore({
        reducer: persistedReducer,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                immutableCheck: false,
                serializableCheck: false,
            })
                .prepend(notificationListener.middleware)
                .concat(RTKMiddleware),
        devTools: isDevelopment(),
    });
};

/**
 * Application store with redux middleware.
 * Use as prop for react-redux Provider component.
 */
export const store = createStore();
export const persistor = persistStore(store);

export type StoreState = ReturnType<typeof store.getState>;
export type StoreDispatch = typeof store.dispatch;
